import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadDocumentBlob } from '../data/documents';
import { fileExists, uploadFile } from '../data/graph';
import { describeApiError } from '../data/http';
import type { SharePointLocation } from '../data/types';

export type SaveStatus = 'pending' | 'saving' | 'saved' | 'failed' | 'conflict';

export interface SaveItem {
  documentId: string;
  fileName: string;
  status: SaveStatus;
  webUrl?: string;
  error?: string;
  /** The user chose to overwrite a file of the same name. */
  replace?: boolean;
  /** The name the user chose after a conflict. */
  saveAs?: string;
}

export type ConflictChoice = { replace: true } | { saveAs: string };

/** Few enough to stay clear of Graph throttling, enough to keep a batch moving. */
const concurrency = 3;

/**
 * Saves one or many generated documents to a single SharePoint location. Runs in the
 * browser (delegated token), so each file is fetched from the API and then uploaded.
 * A file that already exists waits for the user to replace it or pick a new name.
 */
export function useSharePointSave(
  documents: { documentId: string; fileName: string }[],
  onSaved?: (location: SharePointLocation) => void
) {
  const { t } = useTranslation();

  const [items, setItems] = useState<SaveItem[]>(() =>
    documents.map((document) => ({ ...document, status: 'pending' as const }))
  );
  const [running, setRunning] = useState(false);

  const update = (documentId: string, patch: Partial<SaveItem>) => {
    setItems((current) => current.map((item) => (item.documentId === documentId ? { ...item, ...patch } : item)));
  };

  const run = async (location: SharePointLocation, candidates: SaveItem[]) => {
    const queue = candidates.filter((item) => item.status !== 'saved');
    const queued = new Set(queue.map((item) => item.documentId));

    setRunning(true);
    setItems((current) =>
      current.map((item) => (queued.has(item.documentId) ? { ...item, status: 'pending', error: undefined } : item))
    );

    const worker = async () => {
      for (let next = queue.shift(); next; next = queue.shift()) {
        const { documentId } = next;
        const name = next.saveAs ?? next.fileName;
        update(documentId, { status: 'saving' });

        try {
          if (!next.replace && (await fileExists(location, name))) {
            update(documentId, { status: 'conflict' });
            continue;
          }

          const content = await downloadDocumentBlob(documentId);
          const uploaded = await uploadFile(location, name, content);
          update(documentId, { status: 'saved', webUrl: uploaded.webUrl });
          onSaved?.(location);
        } catch (error) {
          update(documentId, { status: 'failed', error: describeApiError(error, t('sharePoint.save.failed')) });
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
    setRunning(false);
  };

  /** Skips files already saved, so the same call retries only the failures. */
  const save = (location: SharePointLocation) => run(location, items);

  const resolveConflict = (location: SharePointLocation, documentId: string, choice: ConflictChoice) => {
    const item = items.find((candidate) => candidate.documentId === documentId);
    if (!item) return Promise.resolve();

    const resolved: SaveItem = { ...item, replace: 'replace' in choice, saveAs: 'saveAs' in choice ? choice.saveAs : item.saveAs };
    update(documentId, { replace: resolved.replace, saveAs: resolved.saveAs });
    return run(location, [resolved]);
  };

  const savedCount = items.filter((item) => item.status === 'saved').length;
  const failedCount = items.filter((item) => item.status === 'failed').length;
  const conflictCount = items.filter((item) => item.status === 'conflict').length;

  return { items, running, save, resolveConflict, savedCount, failedCount, conflictCount };
}
